/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React from 'react';
import { AlertCircle, ExternalLink, X } from 'lucide-react';
import { DemoUser } from '../utils/demoMode';

interface DemoBannerProps {
  demoUser: DemoUser;
  onDismiss?: () => void;
}

const DemoBanner: React.FC<DemoBannerProps> = ({ demoUser, onDismiss }) => {
  const expiresDate = new Date(demoUser.demoExpiresAt);
  const isPermanent = expiresDate.getFullYear() > 2100; // 100 years = permanent
  const daysUntilExpiry = Math.ceil((expiresDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-sm">
              🎸 {demoUser.demoLabel}
            </p>
            <p className="text-xs text-blue-100">
              {isPermanent
                ? 'Full Pro features unlocked • No sign-up required'
                : `${daysUntilExpiry} days remaining • Full Pro features unlocked • No sign-up required`}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={demoUser.demoLabel.toLowerCase().includes('beta')
              ? "mailto:hello@guituitive.com?subject=Beta Feedback"
              : "/pricing"
            }
            className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            {demoUser.demoLabel.toLowerCase().includes('beta') ? 'Send Feedback' : 'Get Full Access'}
          </a>

          {onDismiss && (
            <button
              onClick={onDismiss}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
